import React, { useCallback, useMemo, useState } from "react";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import useMediaQuery from "@mui/material/useMediaQuery";

import CartItemsHeader from "src/views/shopping/Cart/CartItemsHeader";
import Dimensions from "src/views/inventory/requests/Dimensions";
import ItemId from "src/views/shopping/Orders/ItemId";
import { getImage } from "src/views/shopping/Cart/CartItemFields";
import ConfirmDeleteModal from "src/components/images/ConfirmDeleteModal";
import CartItemQuantity from "src/views/inventory/requests/CartItemQuantity";
import useMutation from "src/hooks/useMutation";
import {
  DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
} from "src/constants/graphql/mutations";
import useNotification from "src/hooks/notification";
import InternalWorkOrderGuidedWizardModal from "src/views/inventory/requests/orders/internal-work-order-guided-wizard/InternalWorkOrderGuidedWizardModal";
import { useNavigate } from "react-router";
import useQueryParams from "src/hooks/useQueryParams";
import useRoles from "src/hooks/user/useRoles";
import InternalWorkOrderItemMobileCard from "src/views/inventory/requests/InternalWorkOrderItemMobileCard";

export default function InternalWorkOrderItems({
  display,
  items = [],
  onDelete,
  internalWorkOrderId,
  internalWorkOrder,
}) {
  const { notifyError } = useNotification();
  const navigate = useNavigate();
  const hasAssistantPermission = useRoles(["admin", "manager", "inventory"]);
  const params = useQueryParams();
  const showGuidedWizard = params.get("internalWorkOrderGuidedWizard");
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteInternalWorkOrderItem, { loading: deleteLoading }] = useMutation(
    DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION
  );
  const [updateInternalWorkOrderItem, { loading: updateLoading }] = useMutation(
    UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION
  );
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("sm"));

  const filtered = useMemo(() => {
    if (!searchTerm) return items;
    const term = searchTerm.toLowerCase();
    return items.filter(it => {
      const a = it.asset || {};
      return (
        a.itemId?.toLowerCase().includes(term) ||
        a.assetId?.toLowerCase().includes(term) ||
        a.description?.toLowerCase().includes(term) ||
        a.model?.toLowerCase().includes(term) ||
        a.skuNumber?.toLowerCase().includes(term)
      );
    });
  }, [items, searchTerm]);

  const handleDelete = useCallback(
    async id => {
      await deleteInternalWorkOrderItem({
        variables: { id },
        onSuccess: async () => {
          if (onDelete) onDelete();
          setShowDeleteModal(null);
        },
        onFailure: error => {
          console.error("Failed to delete internal work order item:", error);
        },
      });
    },
    [deleteInternalWorkOrderItem, onDelete]
  );

  const handleQuantityChange = useCallback(
    async (item, newQuantity) => {
      const input = {
        quantity: newQuantity,
      };

      await updateInternalWorkOrderItem({
        variables: { id: item.id, input },
        onSuccess: async () => {
          if (onDelete) onDelete();
        },
        onFailure: () => {
          notifyError("Failed to update internal work order item quantity");
        },
      });
    },
    [updateInternalWorkOrderItem, onDelete, internalWorkOrderId]
  );

  const displayColumns = useMemo(() => {
    const base = display || {};
    const hasSkuNumber = filtered.some(it => !!it?.asset?.skuNumber);
    const hasBarcode = filtered.some(it => !!it?.asset?.barcode);
    const hasModel = filtered.some(it => !!it?.asset?.model);
    const hasDimensions = filtered.some(it => {
      const dimensions = it?.asset?.dimensions;
      if (!Array.isArray(dimensions) || dimensions.length === 0) return false;

      return dimensions.some(({ width, depth, height }) =>
        [width, depth, height].some(value => Number(value) > 0)
      );
    });

    return {
      ...base,
      ii_image: true,
      ii_item_id: true,
      ii_description: true,
      ii_qtyRequested: true,
      ii_sku_number: hasSkuNumber,
      ii_barcode: hasBarcode,
      ii_model: hasModel,
      ii_dimensions: hasDimensions,
    };
  }, [display, filtered]);

  const handleOpenAssistant = () => {
    const newParams = new URLSearchParams(params);
    newParams.set("internalWorkOrderGuidedWizard", internalWorkOrderId);
    newParams.set("step", "details");
    navigate(`?${newParams.toString()}`);
  };

  const handleCloseWizard = () => {
    const newParams = new URLSearchParams(params);
    newParams.delete("internalWorkOrderGuidedWizard");
    newParams.delete("step");
    navigate(`?${newParams.toString()}`);
    if (onDelete) onDelete(); // Refresh the list when wizard closes
  };

  return (
    <Box>
      <Box
        pb={2}
        pt={0.5}
        display="flex"
        justifyContent="space-between"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={1}
        alignItems={{ xs: "flex-start", sm: "center" }}
      >
        <TextField
          size="small"
          placeholder="Search"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
        {hasAssistantPermission && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAssistant}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            Internal Order Assistant
          </Button>
        )}
      </Box>
      <Card>
        {filtered.length === 0 ? (
          <Box p={2} textAlign="center">
            <Typography variant="body2" color="textSecondary">
              No items found{searchTerm ? ` matching "${searchTerm}"` : ""}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box>
            {filtered.map(item => (
              <InternalWorkOrderItemMobileCard
                key={`${item.id}-${item.asset?.id || "asset"}`}
                item={item}
                display={displayColumns}
                onDelete={onDelete}
              />
            ))}
          </Box>
        ) : (
          <Paper sx={{ overflow: "hidden" }}>
            <Table
              sx={{
                tableLayout: "fixed",
                width: "100%",
                "& .MuiTableCell-root": {
                  px: 1,
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                  verticalAlign: "top",
                  textAlign: "center",
                },
              }}
            >
              <CartItemsHeader display={displayColumns} centerAligned />
              <TableBody>
                {filtered.map(item => {
                  const asset = item.asset || {};
                  const photo = getImage(asset);
                  const isDeleting = deleteLoading && showDeleteModal === item.id;
                  const isNonAllocating = item.nonAllocating;
                  return (
                    <TableRow key={item.id}>
                      {displayColumns.ii_image && (
                        <TableCell>
                          {photo ? <img style={{ maxWidth: 90 }} src={photo} /> : "No image"}
                        </TableCell>
                      )}
                      {displayColumns.ii_item_id && (
                        <TableCell>
                          <ItemId asset={asset} />
                        </TableCell>
                      )}
                      {displayColumns.ii_sku_number && (
                        <TableCell>{asset.skuNumber}</TableCell>
                      )}
                      {displayColumns.ii_barcode && <TableCell>{asset.barcode}</TableCell>}
                      {displayColumns.ii_description && <TableCell>{asset.description}</TableCell>}
                      {displayColumns.ii_model && <TableCell>{asset.model}</TableCell>}
                      {displayColumns.ii_dimensions && (
                        <TableCell>
                          <Dimensions dimensions={asset.dimensions} />
                        </TableCell>
                      )}
                      {displayColumns.ii_qtyRequested && (
                        <TableCell>
                          {isNonAllocating ? (
                            <Chip label="Non-allocating" size="small" variant="outlined" />
                          ) : (
                            <CartItemQuantity
                              disabled={updateLoading || deleteLoading}
                              value={item.quantity || ""}
                              availableQuantity={item.asset?.availableQuantity}
                              onChange={newQuantity => handleQuantityChange(item, newQuantity)}
                            />
                          )}
                        </TableCell>
                      )}
                      <TableCell sx={{ width: 56, textAlign: "center" }}>
                        <Tooltip arrow title="Remove Item">
                          <IconButton
                            disabled={deleteLoading}
                            onClick={() => setShowDeleteModal(item.id)}
                          >
                            {isDeleting ? <CircularProgress size={24} /> : <DeleteIcon />}
                          </IconButton>
                        </Tooltip>
                        {showDeleteModal === item.id && (
                          <ConfirmDeleteModal
                            title="Remove Item"
                            applyLabel="Remove"
                            descr="Are you sure you want to remove the selected item from this internal work order?"
                            onClose={() => setShowDeleteModal(null)}
                            onDelete={() => handleDelete(item.id)}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Card>

      {showGuidedWizard && internalWorkOrder && (
        <InternalWorkOrderGuidedWizardModal
          internalWorkOrder={internalWorkOrder}
          onToggle={handleCloseWizard}
        />
      )}
    </Box>
  );
}
